// ============================================================
// adminPage.js — Lógica de las páginas de administración
// ============================================================
import { qs, on } from './utils.js';
import { toast } from './toast.js';
import { apiRequest } from './api.js';

export function initAdminPage() {
  const page = document.body.dataset.page;
  
  if (page === 'admin-newsletter') {
    initNewsletterAdmin();
  } else if (page === 'admin-suscripciones') {
    initSuscripcionesAdmin();
  } else if (page === 'admin-cuotas') {
    initAiQuotasAdmin();
  }
}

// ── ADMINISTRACIÓN DE NEWSLETTER ──────────────────────────────
async function initNewsletterAdmin() {
  console.log('[Admin] Inicializando panel de Newsletter...');

  let allSubscribers = [];

  const tableBody = qs('#subscribersTableBody');
  const searchInput = qs('#adminSearchInput');
  const countDisplay = qs('#subscribersCount');
  const countNewDisplay = qs('#newSubscribersCount');

  // Cargar datos
  async function loadSubscribers() {
    try {
      if (tableBody) tableBody.innerHTML = '<tr><td colspan="3" style="padding: 24px; text-align: center; color: #8e90a6;">Cargando suscriptores...</td></tr>';
      
      const subscribers = await apiRequest('/newsletter/subscribers');
      allSubscribers = subscribers || [];
      
      renderStats();
      renderTable(allSubscribers);
    } catch (err) {
      console.error('[Admin] Error cargando suscriptores:', err);
      toast({ message: err.message || 'Error al cargar los suscriptores de la newsletter.', type: 'error' });
      if (tableBody) {
        tableBody.innerHTML = '<tr><td colspan="3" style="padding: 24px; text-align: center; color: #ff5b5b;">No se pudieron cargar los datos de la newsletter.</td></tr>';
      }
    }
  }

  // Estadísticas
  function renderStats() {
    if (countDisplay) countDisplay.textContent = allSubscribers.length;
    
    if (countNewDisplay) {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const newSubs = allSubscribers.filter(sub => {
        const createdDate = new Date(sub.createdAt);
        return createdDate >= sevenDaysAgo;
      });
      
      countNewDisplay.textContent = newSubs.length;
    }
  }

  // Renderizar tabla
  function renderTable(list) {
    if (!tableBody) return;

    if (list.length === 0) {
      tableBody.innerHTML = '<tr><td colspan="3" style="padding: 24px; text-align: center; color: #8e90a6;">No se encontraron suscriptores.</td></tr>';
      return;
    }

    tableBody.innerHTML = list.map(sub => {
      const date = new Date(sub.createdAt).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      return `
        <tr style="border-bottom: 1px solid #25263b; transition: background 0.2s;" onmouseover="this.style.background='rgba(255,255,255,0.02)'" onmouseout="this.style.background='transparent'">
          <td style="padding: 14px 18px; color: #e1e1e6; font-size: 0.95rem; font-family: monospace;">${sub.email}</td>
          <td style="padding: 14px 18px; color: #a0a1b5; font-size: 0.9rem;">${date}</td>
          <td style="padding: 14px 18px; text-align: right;">
            <button class="btn-vimo btn-vimo-secondary btn-vimo-sm" type="button" 
              data-delete-subscriber="${sub.id}"
              style="padding: 4px 8px; border-color: rgba(255,91,91,0.2); color: #ff5b5b; font-size: 0.8rem; background: transparent; cursor: pointer; border-radius: 4px; transition: all 0.2s;"
              onmouseover="this.style.background='rgba(255,91,91,0.1)';" 
              onmouseout="this.style.background='transparent';">
              <i class="bi bi-trash"></i> Eliminar
            </button>
          </td>
        </tr>
      `;
    }).join('');
  }

  // Buscador
  if (searchInput) {
    searchInput.addEventListener('input', (e) => {
      const query = e.target.value.toLowerCase().trim();
      const filtered = allSubscribers.filter(sub => sub.email.toLowerCase().includes(query));
      renderTable(filtered);
    });
  }

  // Eliminar suscriptor
  on(document, 'click', '[data-delete-subscriber]', async (e, btn) => {
    const id = btn.getAttribute('data-delete-subscriber');
    if (!id) return;
    
    const { showConfirmModal } = await import('./subscriptionModal.js');
    const confirmed = await showConfirmModal({
      title: '¿Eliminar suscriptor?',
      message: '¿Estás seguro de que deseas eliminar este suscriptor de la newsletter?',
      confirmText: 'Sí, eliminar',
      cancelText: 'Cancelar'
    });
    if (!confirmed) return;
    
    try {
      btn.disabled = true;
      btn.textContent = 'Eliminando...';
      
      await apiRequest(`/newsletter/subscribers/${id}`, {
        method: 'DELETE'
      });
      
      toast({ message: 'Suscriptor eliminado correctamente.', type: 'success' });
      loadSubscribers();
    } catch (err) {
      console.error('[Admin] Error eliminando suscriptor:', err);
      toast({ message: err.message || 'Error al eliminar el suscriptor.', type: 'error' });
      btn.disabled = false;
      btn.innerHTML = '<i class="bi bi-trash"></i> Eliminar';
    }
  });

  loadSubscribers();
}

// ── ADMINISTRACIÓN DE SUSCRIPCIONES DE PAGO ────────────────────
async function initSuscripcionesAdmin() {
  console.log('[Admin] Inicializando panel de Suscripciones...');

  let allUsers = [];

  const usersTableBody = qs('#usersTableBody');
  const usersSearchInput = qs('#adminUsersSearchInput');

  // Cargar datos
  async function loadUsers() {
    try {
      if (usersTableBody) usersTableBody.innerHTML = '<tr><td colspan="5" style="padding: 24px; text-align: center; color: #8e90a6;">Cargando usuarios y suscripciones...</td></tr>';
      
      const users = await apiRequest('/subscriptions/admin/list');
      allUsers = users || [];
      
      renderUsersTable(allUsers);
    } catch (err) {
      console.error('[Admin] Error cargando usuarios:', err);
      toast({ message: err.message || 'Error al cargar los usuarios y suscripciones.', type: 'error' });
      if (usersTableBody) {
        usersTableBody.innerHTML = '<tr><td colspan="5" style="padding: 24px; text-align: center; color: #ff5b5b;">No se pudieron cargar las suscripciones de pago.</td></tr>';
      }
    }
  }

  // Renderizar tabla
  function renderUsersTable(list) {
    if (!usersTableBody) return;

    if (list.length === 0) {
      usersTableBody.innerHTML = '<tr><td colspan="6" style="padding: 24px; text-align: center; color: #8e90a6;">No se encontraron usuarios.</td></tr>';
      return;
    }

    usersTableBody.innerHTML = list.map(user => {
      const regDate = new Date(user.createdAt).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });

      const latestSub = user.subscriptions && user.subscriptions[0];
      let subBadge = '<span style="color: #8e90a6; font-size: 0.85rem; padding: 2px 6px; background: rgba(142, 144, 166, 0.1); border-radius: 4px;">Ninguna</span>';
      if (latestSub) {
        if (latestSub.status === 'active') {
          subBadge = '<span style="color: #10b981; font-size: 0.85rem; padding: 2px 6px; background: rgba(16, 185, 129, 0.1); border-radius: 4px; font-weight: bold;"><i class="bi bi-check-circle"></i> Aprobado</span>';
        } else if (latestSub.status === 'incomplete') {
          subBadge = '<span style="color: #f59e0b; font-size: 0.85rem; padding: 2px 6px; background: rgba(245, 158, 11, 0.1); border-radius: 4px;"><i class="bi bi-exclamation-triangle"></i> Incompleto</span>';
        } else if (latestSub.status === 'canceled') {
          subBadge = '<span style="color: #ff5b5b; font-size: 0.85rem; padding: 2px 6px; background: rgba(255, 91, 91, 0.1); border-radius: 4px;"><i class="bi bi-x-circle"></i> Cancelado</span>';
        }
      }

      // Región Badge
      const isBr = user.region === 'BR';
      const regionBadge = isBr
        ? '<span style="color: #6366f1; font-size: 0.85rem; padding: 2px 6px; background: rgba(99, 102, 241, 0.1); border-radius: 4px; font-weight: bold;">🇧🇷 Brasil</span>'
        : '<span style="color: #ec4899; font-size: 0.85rem; padding: 2px 6px; background: rgba(236, 72, 153, 0.1); border-radius: 4px; font-weight: bold;">🇪🇸 Latam/España</span>';

      // Plan/Estado Badge
      const isSubscribed = user.plan === 'suscrito' || user.plan === 'active' || (latestSub && latestSub.status === 'active');
      const planBadge = isSubscribed
        ? '<span style="color: #10b981; font-size: 0.85rem; padding: 2px 6px; background: rgba(16, 185, 129, 0.1); border-radius: 4px; font-weight: bold;"><i class="bi bi-star-fill"></i> Suscrito</span>'
        : '<span style="color: #f59e0b; font-size: 0.85rem; padding: 2px 6px; background: rgba(245, 158, 11, 0.1); border-radius: 4px; font-weight: bold;"><i class="bi bi-clock"></i> Trial</span>';

      // Precio Suscripción
      const priceText = isBr ? '$19,90 USD/mes' : '$29,90 USD/mes';
      const priceColor = isBr ? '#a5b4fc' : '#f9a8d4';
      const priceBadge = `<span style="color: ${priceColor}; font-size: 0.9rem; font-weight: bold;">${priceText}</span>`;

      const actionHtml = user.isTest
        ? `
          <div style="display: flex; align-items: center; justify-content: flex-end; gap: 8px;">
            <span style="color: #f59e0b; font-size: 0.75rem; padding: 2px 4px; border: 1px solid rgba(245,158,11,0.3); border-radius: 4px; font-weight: bold; background: rgba(245,158,11,0.05);">PRUEBA</span>
            <button class="btn-vimo btn-vimo-secondary btn-vimo-sm" type="button" 
              data-delete-user="${user.id}"
              style="padding: 4px 8px; border-color: rgba(255,91,91,0.2); color: #ff5b5b; font-size: 0.8rem; background: transparent; cursor: pointer; border-radius: 4px; transition: all 0.2s;"
              onmouseover="this.style.background='rgba(255,91,91,0.1)';" 
              onmouseout="this.style.background='transparent';">
              <i class="bi bi-trash"></i> Eliminar
            </button>
          </div>
        `
        : `
          <div style="display: flex; align-items: center; justify-content: flex-end; gap: 8px;">
            <span style="color: #10b981; font-size: 0.75rem; padding: 2px 4px; border: 1px solid rgba(16,185,129,0.3); border-radius: 4px; font-weight: bold; background: rgba(16,185,129,0.05);">REAL</span>
          </div>
        `;

      return `
        <tr style="border-bottom: 1px solid #25263b; transition: background 0.2s;" onmouseover="this.style.background='rgba(255,255,255,0.02)'" onmouseout="this.style.background='transparent'">
          <td style="padding: 14px 18px;">
            <div style="font-weight: 600; color: #fff;">${user.name || user.email.split('@')[0]}</div>
            <div style="font-size: 0.8rem; color: #8e90a6; font-family: monospace; margin-top: 2px;">${user.email}</div>
          </td>
          <td style="padding: 14px 18px;">${regionBadge}</td>
          <td style="padding: 14px 18px;">${planBadge}</td>
          <td style="padding: 14px 18px;">${priceBadge}</td>
          <td style="padding: 14px 18px;">${subBadge}</td>
          <td style="padding: 14px 18px; color: #a0a1b5; font-size: 0.9rem;">${regDate}</td>
          <td style="padding: 14px 18px; text-align: right;">${actionHtml}</td>
        </tr>
      `;
    }).join('');
  }

  // Buscador
  if (usersSearchInput) {
    usersSearchInput.addEventListener('input', (e) => {
      const query = e.target.value.toLowerCase().trim();
      const filtered = allUsers.filter(user => 
        user.email.toLowerCase().includes(query) || 
        (user.name && user.name.toLowerCase().includes(query)) ||
        user.plan.toLowerCase().includes(query) ||
        (user.region && user.region.toLowerCase().includes(query))
      );
      renderUsersTable(filtered);
    });
  }

  // Eliminar usuario de prueba
  on(document, 'click', '[data-delete-user]', async (e, btn) => {
    const id = btn.getAttribute('data-delete-user');
    if (!id) return;
    
    const { showConfirmModal } = await import('./subscriptionModal.js');
    const confirmed = await showConfirmModal({
      title: '¿Eliminar usuario?',
      message: '¿Estás seguro de que deseas eliminar esta cuenta de prueba de la base de datos?',
      confirmText: 'Sí, eliminar',
      cancelText: 'Cancelar'
    });
    if (!confirmed) return;
    
    try {
      btn.disabled = true;
      btn.textContent = 'Eliminando...';
      
      await apiRequest(`/subscriptions/admin/users/${id}`, {
        method: 'DELETE'
      });
      
      toast({ message: 'Usuario de prueba eliminado correctamente.', type: 'success' });
      loadUsers();
    } catch (err) {
      console.error('[Admin] Error eliminando usuario:', err);
      toast({ message: err.message || 'Error al eliminar el usuario.', type: 'error' });
      btn.disabled = false;
      btn.innerHTML = '<i class="bi bi-trash"></i> Eliminar';
    }
  });

  loadUsers();
}

// ── ADMINISTRACIÓN DE CUOTAS DE IAS ───────────────────────────
async function initAiQuotasAdmin() {
  console.log('[Admin] Inicializando panel de Cuotas de IAs...');

  const btnRefresh = qs('#btnRefreshQuotas');
  const alertBanner = qs('#aiQuotaAlertBanner');
  const alertText = qs('#aiQuotaAlertText');

  const falCostDisplay = qs('#falCostDisplay');
  const falProgressBar = qs('#falProgressBar');
  const falImagesCount = qs('#falImagesCount');
  const falVideosCount = qs('#falVideosCount');
  const falStatusBadge = qs('#falStatusBadge');

  const heyGenMinutesDisplay = qs('#heyGenMinutesDisplay');
  const heyGenProgressBar = qs('#heyGenProgressBar');
  const heyGenAvatarsCount = qs('#heyGenAvatarsCount');
  const heyGenStatusBadge = qs('#heyGenStatusBadge');

  const elevenCharsDisplay = qs('#elevenCharsDisplay');
  const elevenProgressBar = qs('#elevenProgressBar');
  const elevenPercentRemaining = qs('#elevenPercentRemaining');
  const elevenResetDate = qs('#elevenResetDate');
  const elevenStatusBadge = qs('#elevenStatusBadge');

  const aiJobsTableBody = qs('#aiJobsTableBody');

  async function loadAiQuotasData(isManualRefresh = false) {
    try {
      if (btnRefresh) {
        btnRefresh.disabled = true;
        btnRefresh.innerHTML = '<i class="bi bi-arrow-clockwise spin" style="animation: spin 1s linear infinite;"></i> Consultando...';
      }

      if (aiJobsTableBody && !isManualRefresh) {
        aiJobsTableBody.innerHTML = '<tr><td colspan="5" style="padding: 24px; text-align: center; color: #8e90a6;">Cargando estado y cuotas de las IAs...</td></tr>';
      }

      const res = await apiRequest('/admin/ai-quotas');

      // 1. Renderizar alertas si existen
      if (alertBanner && alertText) {
        const alerts = res.summary?.alerts || [];
        if (alerts.length > 0) {
          alertBanner.style.display = 'block';
          alertText.innerHTML = alerts.map(a => `<div>• <strong>${a.provider}:</strong> ${a.message}</div>`).join('');
        } else {
          alertBanner.style.display = 'none';
        }
      }

      // 2. Renderizar fal.ai
      const fal = res.providers?.fal || {};
      if (falCostDisplay) falCostDisplay.textContent = `$${(fal.estimatedCostUSD || 0).toFixed(2)} USD`;
      if (falImagesCount) falImagesCount.textContent = fal.imagesGenerated30d || 0;
      if (falVideosCount) falVideosCount.textContent = fal.videosGenerated30d || 0;
      if (falProgressBar) {
        const totalOps = (fal.imagesGenerated30d || 0) + (fal.videosGenerated30d || 0);
        const pct = Math.min(100, Math.max(5, Math.round((totalOps / 200) * 100)));
        falProgressBar.style.width = `${pct}%`;
      }
      if (falStatusBadge) {
        if (fal.healthy) {
          falStatusBadge.className = '';
          falStatusBadge.style.color = '#10b981';
          falStatusBadge.style.background = 'rgba(16, 185, 129, 0.1)';
          falStatusBadge.innerHTML = '<i class="bi bi-circle-fill" style="font-size: 0.5rem;"></i> Operativo 🟢';
        } else {
          falStatusBadge.style.color = '#ff5b5b';
          falStatusBadge.style.background = 'rgba(255, 91, 91, 0.1)';
          falStatusBadge.innerHTML = '<i class="bi bi-x-circle-fill" style="font-size: 0.5rem;"></i> Sin API Key 🚨';
        }
      }

      // 3. Renderizar HeyGen
      const heygen = res.providers?.heygen || {};
      if (heyGenMinutesDisplay) heyGenMinutesDisplay.textContent = `~${heygen.estimatedVideoMinutesRemaining || 1500} min`;
      if (heyGenAvatarsCount) heyGenAvatarsCount.textContent = heygen.totalAvatarGenerations30d || 0;
      if (heyGenProgressBar) {
        const pct = Math.min(100, Math.max(8, Math.round(((heygen.totalAvatarGenerations30d || 0) / 100) * 100)));
        heyGenProgressBar.style.width = `${pct}%`;
      }
      if (heyGenStatusBadge) {
        if (heygen.healthy) {
          heyGenStatusBadge.style.color = '#10b981';
          heyGenStatusBadge.style.background = 'rgba(16, 185, 129, 0.1)';
          heyGenStatusBadge.innerHTML = '<i class="bi bi-circle-fill" style="font-size: 0.5rem;"></i> Operativo 🟢';
        } else {
          heyGenStatusBadge.style.color = '#ff5b5b';
          heyGenStatusBadge.style.background = 'rgba(255, 91, 91, 0.1)';
          heyGenStatusBadge.innerHTML = '<i class="bi bi-x-circle-fill" style="font-size: 0.5rem;"></i> Sin API Key 🚨';
        }
      }

      // 4. Renderizar ElevenLabs (Tiempo Real)
      const el = res.providers?.elevenlabs || {};
      const countFormatted = (el.characterCount || 0).toLocaleString();
      const limitFormatted = (el.characterLimit || 100000).toLocaleString();
      
      if (elevenCharsDisplay) elevenCharsDisplay.textContent = `${countFormatted} / ${limitFormatted}`;
      if (elevenPercentRemaining) elevenPercentRemaining.textContent = `${el.percentRemaining}%`;
      if (elevenResetDate) elevenResetDate.textContent = el.resetDate || 'Mensual';
      if (elevenProgressBar) {
        const usedPct = 100 - (el.percentRemaining ?? 100);
        elevenProgressBar.style.width = `${usedPct}%`;
        if (usedPct > 80) {
          elevenProgressBar.style.background = 'linear-gradient(90deg, #f59e0b, #ff5b5b)';
        } else {
          elevenProgressBar.style.background = 'linear-gradient(90deg, #10b981, #34d399)';
        }
      }
      if (elevenStatusBadge) {
        if (el.healthy) {
          elevenStatusBadge.style.color = '#10b981';
          elevenStatusBadge.style.background = 'rgba(16, 185, 129, 0.1)';
          elevenStatusBadge.innerHTML = '<i class="bi bi-check-circle-fill" style="font-size: 0.8rem;"></i> Conectado 🟢';
        } else {
          elevenStatusBadge.style.color = '#f59e0b';
          elevenStatusBadge.style.background = 'rgba(245, 158, 11, 0.1)';
          elevenStatusBadge.innerHTML = '<i class="bi bi-exclamation-triangle-fill" style="font-size: 0.8rem;"></i> Alerta Cuotas ⚠️';
        }
      }

      // 5. Renderizar Tabla de Procesamiento de IAs
      renderJobsTable(res.recentJobs || []);

      if (isManualRefresh) {
        toast({ message: 'Cuotas y estado de APIs actualizados correctamente.', type: 'success' });
      }
    } catch (err) {
      console.error('[Admin AI Quotas] Error al consultar cuotas:', err);
      toast({ message: err.message || 'Error al consultar cuotas de IA.', type: 'error' });
      if (aiJobsTableBody) {
        aiJobsTableBody.innerHTML = '<tr><td colspan="5" style="padding: 24px; text-align: center; color: #ff5b5b;">Fallo al cargar el estado de las APIs.</td></tr>';
      }
    } finally {
      if (btnRefresh) {
        btnRefresh.disabled = false;
        btnRefresh.innerHTML = '<i class="bi bi-arrow-clockwise"></i> Actualizar Cuotas';
      }
    }
  }

  function renderJobsTable(jobsList) {
    if (!aiJobsTableBody) return;

    if (jobsList.length === 0) {
      aiJobsTableBody.innerHTML = '<tr><td colspan="5" style="padding: 24px; text-align: center; color: #8e90a6;">No se registran solicitudes de procesado de IA recientemente.</td></tr>';
      return;
    }

    aiJobsTableBody.innerHTML = jobsList.map(job => {
      const dateStr = new Date(job.createdAt).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      let typeBadge = '';
      if (job.type === 'image') {
        typeBadge = '<span style="color: #6366f1; font-size: 0.8rem; padding: 2px 6px; background: rgba(99,102,241,0.1); border-radius: 4px; font-weight: bold;"><i class="bi bi-image"></i> Imagen</span>';
      } else if (job.type === 'video') {
        typeBadge = '<span style="color: #a855f7; font-size: 0.8rem; padding: 2px 6px; background: rgba(168,85,247,0.1); border-radius: 4px; font-weight: bold;"><i class="bi bi-play-btn"></i> Vídeo</span>';
      } else if (job.type === 'avatar') {
        typeBadge = '<span style="color: #ec4899; font-size: 0.8rem; padding: 2px 6px; background: rgba(236,72,153,0.1); border-radius: 4px; font-weight: bold;"><i class="bi bi-person-circle"></i> Avatar</span>';
      } else {
        typeBadge = `<span style="color: #10b981; font-size: 0.8rem; padding: 2px 6px; background: rgba(16,185,129,0.1); border-radius: 4px; font-weight: bold;">${job.type}</span>`;
      }

      let statusBadge = '';
      if (job.status === 'completed') {
        statusBadge = '<span style="color: #10b981; font-size: 0.8rem; padding: 2px 6px; background: rgba(16,185,129,0.1); border-radius: 4px; font-weight: bold;">Completado</span>';
      } else if (job.status === 'failed') {
        statusBadge = '<span style="color: #ff5b5b; font-size: 0.8rem; padding: 2px 6px; background: rgba(255,91,91,0.1); border-radius: 4px; font-weight: bold;">Fallido</span>';
      } else {
        statusBadge = '<span style="color: #f59e0b; font-size: 0.8rem; padding: 2px 6px; background: rgba(245,158,11,0.1); border-radius: 4px; font-weight: bold;">Procesando</span>';
      }

      const providerName = (job.type === 'avatar' ? 'HeyGen' : (job.type === 'speech' ? 'ElevenLabs' : 'fal.ai'));

      return `
        <tr style="border-bottom: 1px solid #25263b; transition: background 0.2s;" onmouseover="this.style.background='rgba(255,255,255,0.02)'" onmouseout="this.style.background='transparent'">
          <td style="padding: 14px 18px; color: #fff; font-size: 0.9rem;">${job.userEmail}</td>
          <td style="padding: 14px 18px;">${typeBadge}</td>
          <td style="padding: 14px 18px; color: #a0a1b5; font-size: 0.85rem; font-family: monospace;">
            <strong style="color: #e1e1e6;">${providerName}</strong> (${job.model || 'Standard'})
          </td>
          <td style="padding: 14px 18px;">${statusBadge}</td>
          <td style="padding: 14px 18px; color: #8e90a6; font-size: 0.85rem; text-align: right;">${dateStr}</td>
        </tr>
      `;
    }).join('');
  }

  if (btnRefresh) {
    btnRefresh.addEventListener('click', () => loadAiQuotasData(true));
  }

  loadAiQuotasData();
}

